import { FC } from 'react';
import { CheckRounded, ContentCopyRounded } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import './TeamInviteModal.scss';
import { BasicDialog } from '../../common';

interface TeamInviteModalProps {
  open: boolean;
  teamName: string;
  inviteEmail: string;
  inviteError: string | null;
  generatedInviteUrl: string | null;
  isGeneratingInviteLink: boolean;
  isCopyingInviteLink: boolean;
  isInviteLinkCopied: boolean;
  isGenerateDisabled: boolean;
  onInviteEmailChange: (value: string) => void;
  onGenerateInviteLink: () => void;
  onCopyInviteLink: () => void;
  onClose: () => void;
}

const TeamInviteModal: FC<TeamInviteModalProps> = ({
  open,
  teamName,
  inviteEmail,
  inviteError,
  generatedInviteUrl,
  isGeneratingInviteLink,
  isCopyingInviteLink,
  isInviteLinkCopied,
  isGenerateDisabled,
  onInviteEmailChange,
  onGenerateInviteLink,
  onCopyInviteLink,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <BasicDialog
      visible={open}
      title={t('team.invite.title', { teamName })}
      ctaString={t('team.invite.generateLink')}
      isLoading={isGeneratingInviteLink}
      disabled={isGenerateDisabled}
      showCloseIcon
      className="team-invite-modal-dialog"
      onYes={onGenerateInviteLink}
      onClose={onClose}
    >
      <div className="team-invite-modal" aria-live="polite">
        <div className="team-invite-modal__header">
          <Typography variant="body2" component="p" className="team-invite-modal__subtitle">
            {t('team.invite.description')}
          </Typography>
        </div>

        <div className="team-invite-modal__form">
          <div className="team-invite-modal__field">
            <input
              id="team-invite-email"
              type="email"
              value={inviteEmail}
              className="team-invite-modal__input"
              placeholder="name@company.com"
              autoComplete="email"
              autoFocus
              aria-label={t('team.invite.emailAria')}
              onChange={(event) => onInviteEmailChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onGenerateInviteLink();
                }
              }}
            />
          </div>

          {inviteError && (
            <Typography variant="body2" component="p" className="team-invite-modal__error">
              {inviteError}
            </Typography>
          )}

          {generatedInviteUrl && (
            <div className="team-invite-modal__link-block">
              <label htmlFor="team-invite-url" className="team-invite-modal__label">
                <Typography variant="subtitle1" component="span">
                  {t('team.invite.inviteLink')}
                </Typography>
              </label>
              <div className="team-invite-modal__copy-field">
                <input
                  id="team-invite-url"
                  readOnly
                  value={generatedInviteUrl}
                  className="team-invite-modal__copy-input"
                />
                <Button
                  type="button"
                  variant="text"
                  className="team-invite-modal__copy-button"
                  disabled={isCopyingInviteLink}
                  onClick={onCopyInviteLink}
                >
                  {isInviteLinkCopied ? (
                    <CheckRounded className="team-invite-modal__copy-button-icon" />
                  ) : (
                    <ContentCopyRounded className="team-invite-modal__copy-button-icon" />
                  )}
                  <Typography variant="body2" component="span">
                    {isInviteLinkCopied ? t('team.common.copied') : t('team.common.copy')}
                  </Typography>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </BasicDialog>
  );
};

export default TeamInviteModal;
